/**
 * Mock data of the content generator tutorial (frontend only: nothing is created or fetched).
 * Each scene describes what the content pages render while the tutorial shows it.
 */

export const CONTENT_TUTORIAL_MOCK_CONFIG_ID = "tutorial-config";
export const CONTENT_TUTORIAL_MOCK_PIECE_ID = "tutorial-piece";

const MOCK_CLIENT_ID = "tutorial-client";
const MOCK_CLIENT_NAME = "Clínica Aurora";

const daysFromNow = (days, hour = 10) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, 0, 0, 0);
  return date.toISOString();
};

export const CONTENT_TUTORIAL_MOCK_CLIENT = {
  id: CONTENT_TUTORIAL_MOCK_CONFIG_ID,
  clientId: MOCK_CLIENT_ID,
  name: MOCK_CLIENT_NAME,
  logo: null,
};

const MOCK_CONFIG = {
  id: CONTENT_TUTORIAL_MOCK_CONFIG_ID,
  client: MOCK_CLIENT_ID,
  client_name: MOCK_CLIENT_NAME,
  tone: "friendly",
  language_type: "direct",
  language: "es",
};

const MOCK_PERSONAS = [
  {
    name: "Laura, madre trabajadora",
    age: "38",
    interests: "Salud familiar, organización, citas rápidas",
    painPoints: "Poco tiempo para ir al dentista",
    goals: "Resolver las revisiones de toda la familia en una visita",
  },
  {
    name: "Javier, profesional joven",
    age: "29",
    interests: "Estética dental, tecnología",
    painPoints: "Le preocupa que se noten los brackets",
    goals: "Mejorar su sonrisa de forma discreta",
  },
];

const PIECE_TITLE = "5 señales de que necesitas una revisión dental";

const PIECE_BASE_TEXT =
  "Muchas molestias dentales empiezan sin dolor. Sangrado al cepillarte, sensibilidad al frío o mal aliento persistente son señales de que toca revisión. En Clínica Aurora revisamos tu boca en 20 minutos y te explicamos cada paso.";

const MOCK_PLATFORMS = ["instagram", "facebook", "linkedin"];

const MOCK_BRIEFING = {
  objective: "",
  topic: PIECE_TITLE,
  tone: MOCK_CONFIG.tone,
  languageType: MOCK_CONFIG.language_type,
  language: MOCK_CONFIG.language,
  frequency: 1,
  duration: 1,
};

const listPiece = (id, title, extra) => ({
  id,
  type: "piece",
  title,
  status: "draft",
  display_status: "draft",
  publication_status: "none",
  client_name: MOCK_CLIENT_NAME,
  client_logo: null,
  config: CONTENT_TUTORIAL_MOCK_CONFIG_ID,
  group_id: null,
  has_raw_content: true,
  has_platform_adaptations: true,
  generated_platforms: MOCK_PLATFORMS,
  scheduled_at: null,
  created_at: daysFromNow(-3, 9),
  unopened_unread_notifications: 0,
  ...extra,
});

const LIST_PIECES = [
  listPiece("tutorial-list-1", "Campaña de verano: revisión gratuita", {
    status: "client_approved",
    display_status: "scheduled",
    publication_status: "scheduled",
    scheduled_at: daysFromNow(1, 10),
    created_at: daysFromNow(-6, 9),
  }),
  listPiece("tutorial-list-2", "Conoce a nuestro equipo de ortodoncia", {
    status: "client_approved",
    display_status: "scheduled",
    publication_status: "scheduled",
    generated_platforms: ["instagram", "linkedin"],
    scheduled_at: daysFromNow(3, 18),
    created_at: daysFromNow(-5, 12),
  }),
  listPiece("tutorial-list-3", "Consejos para una sonrisa sana en vacaciones", {
    status: "client_review",
    display_status: "client_review",
    created_at: daysFromNow(-2, 11),
  }),
  listPiece(CONTENT_TUTORIAL_MOCK_PIECE_ID, PIECE_TITLE, {
    generated_platforms: MOCK_PLATFORMS,
    created_at: daysFromNow(0, 9),
  }),
  listPiece("tutorial-list-5", "Ortodoncia invisible: preguntas frecuentes", {
    has_platform_adaptations: false,
    generated_platforms: [],
    created_at: daysFromNow(0, 9),
  }),
];

const LIST_RESPONSE = {
  count: LIST_PIECES.length,
  results: LIST_PIECES,
  status_stats: { draft: 2, client_review: 1, client_approved: 0, scheduled: 2, published: 0 },
};

const UPCOMING_ADAPTATIONS = LIST_PIECES.filter((piece) => piece.scheduled_at).flatMap((piece) =>
  piece.generated_platforms.map((platform) => ({
    id: `${piece.id}-${platform}`,
    platform,
    scheduled_at: piece.scheduled_at,
    content_piece_id: piece.id,
    content_piece_title: piece.title,
    client_name: MOCK_CLIENT_NAME,
    config_id: CONTENT_TUTORIAL_MOCK_CONFIG_ID,
    group_id: null,
  }))
);

const MOCK_LIST = { piecesResponse: LIST_RESPONSE, upcoming: UPCOMING_ADAPTATIONS };

const ADAPTATION_TEXTS = {
  instagram:
    "🦷 ¿Te sangran las encías al cepillarte? Puede ser una señal de alerta.\n\nEstas son 5 señales de que necesitas una revisión dental 👇\n1. Sangrado al cepillarte\n2. Sensibilidad al frío o al calor\n3. Mal aliento persistente\n4. Dolor al masticar\n5. Hace más de un año de tu última visita\n\nReserva tu revisión en Clínica Aurora: enlace en la bio ✨\n\n#saluddental #revisiondental #clinicaaurora",
  facebook:
    "Muchas molestias dentales empiezan sin dolor. Si notas sangrado al cepillarte, sensibilidad o mal aliento persistente, es momento de una revisión.\n\nEn Clínica Aurora revisamos tu boca en 20 minutos y te explicamos cada paso. ¡Pide tu cita!",
  linkedin:
    "La prevención es la mejor inversión en salud. En Clínica Aurora hemos recopilado las 5 señales más habituales que indican que conviene adelantar la revisión dental. Una consulta de 20 minutos puede evitar tratamientos más largos y costosos.",
};

const MOCK_PIECE_BASE = {
  id: `${CONTENT_TUTORIAL_MOCK_PIECE_ID}-base`,
  title: PIECE_TITLE,
  status: "draft",
  display_status: "draft",
  publication_status: "none",
  raw_content: PIECE_BASE_TEXT,
  platform_adaptations: [],
  config: null,
  thread: null,
  group_id: null,
};

const MOCK_PIECE_COMPLETE = {
  ...MOCK_PIECE_BASE,
  id: `${CONTENT_TUTORIAL_MOCK_PIECE_ID}-complete`,
  platform_adaptations: Object.entries(ADAPTATION_TEXTS).map(([platform, text], index) => ({
    id: `tutorial-adaptation-${index + 1}`,
    platform,
    text,
    format: "post",
    images: [],
    call_to_action: null,
    scheduled_at: null,
    social_post: null,
  })),
};

const hoursAgo = (hours) => new Date(Date.now() - hours * 3600000).toISOString();

const MOCK_REVIEW_INSTAGRAM_TEXT = ADAPTATION_TEXTS.instagram.replace(
  "Reserva tu revisión en Clínica Aurora",
  "Primera revisión gratuita en Clínica Aurora: reserva"
);

const MOCK_PIECE_REVIEW = {
  ...MOCK_PIECE_COMPLETE,
  id: `${CONTENT_TUTORIAL_MOCK_PIECE_ID}-review`,
  status: "client_review",
  display_status: "client_review",
  platform_adaptations: MOCK_PIECE_COMPLETE.platform_adaptations.map((adaptation) =>
    adaptation.platform === "instagram" ? { ...adaptation, text: MOCK_REVIEW_INSTAGRAM_TEXT } : adaptation
  ),
};

const MOCK_TEAM_MEMBER = { full_name: "Marta Consultora", role: "consultant" };
const MOCK_CLIENT_USER = { full_name: "Elena Ruiz", role: "client" };

const MOCK_REVIEW_MESSAGES = [
  {
    id: "tutorial-message-1",
    message_type: "event",
    content: "Marta Consultora ha enviado el contenido al cliente",
    created_at: hoursAgo(26),
  },
  {
    id: "tutorial-message-2",
    message_type: "message",
    sender: MOCK_CLIENT_USER,
    content: "¡Me encanta! En Instagram, ¿podemos mencionar que la primera revisión es gratuita?",
    created_at: hoursAgo(5),
  },
  {
    id: "tutorial-message-3",
    message_type: "message",
    sender: MOCK_TEAM_MEMBER,
    content: "¡Hecho, Elena! Ya está cambiado en Instagram. Cuando lo veas bien, apruébalo y lo programamos.",
    created_at: hoursAgo(2),
  },
];

const MOCK_REVIEW_VERSIONS = [
  {
    id: "tutorial-version-2",
    version_number: 2,
    change_type: "manual_edit",
    is_client: false,
    created_by_name: MOCK_TEAM_MEMBER.full_name,
    created_at: hoursAgo(2),
    text_snapshot: MOCK_REVIEW_INSTAGRAM_TEXT,
  },
  {
    id: "tutorial-version-1",
    version_number: 1,
    change_type: "initial",
    is_client: false,
    created_by_name: MOCK_TEAM_MEMBER.full_name,
    created_at: hoursAgo(27),
    text_snapshot: ADAPTATION_TEXTS.instagram,
  },
];

const MOCK_PIECE_SCHEDULING = {
  ...MOCK_PIECE_REVIEW,
  id: `${CONTENT_TUTORIAL_MOCK_PIECE_ID}-scheduling`,
  status: "client_approved",
  display_status: "client_approved",
  scheduled_at: daysFromNow(2, 10),
};

const MOCK_SOCIAL = {
  accounts: MOCK_PLATFORMS.map((platform) => ({
    platform,
    status: "connected",
    display_name: MOCK_CLIENT_NAME,
  })),
};

const listPath = "/HerramientasIA/contenidos";
const GENERATE_PATH = `${listPath}/generar?config=${CONTENT_TUTORIAL_MOCK_CONFIG_ID}&tipo=individual`;
const PIECE_PATH = `${listPath}/pieza/${CONTENT_TUTORIAL_MOCK_PIECE_ID}`;

// The creation page (generar?tipo=individual) with one AI piece.
const singleScene = (phase, extra = {}) => ({
  path: GENERATE_PATH,
  scroll: true,
  mock: {
    campaign: {
      single: true,
      config: MOCK_CONFIG,
      briefing: MOCK_BRIEFING,
      phase,
      topics: [],
      progress: { total: 1, completed: 0, failed: 0, pending: 1, status: "idle" },
      pieces: [],
      selectedChannels: MOCK_PLATFORMS,
      ...extra,
    },
  },
});

/**
 * Scenes of the tutorial: the route it needs and the mock the pages render there.
 * `zIndex` raises the tooltip above a modal; `scroll` lets Joyride scroll to the target.
 */
export const CONTENT_TUTORIAL_SCENES = {
  list: { path: listPath, mock: { list: MOCK_LIST } },
  create_modal: {
    path: listPath,
    zIndex: 100010,
    delay: 350,
    mock: {
      list: MOCK_LIST,
      createModal: {
        client: CONTENT_TUTORIAL_MOCK_CLIENT,
        personas: MOCK_PERSONAS,
        contentType: "single",
      },
    },
  },
  single_briefing: singleScene("briefing"),
  single_base: singleScene("done", {
    progress: { total: 1, completed: 1, failed: 0, pending: 0, status: "completed" },
    pieces: [MOCK_PIECE_BASE],
  }),
  piece_complete: { path: PIECE_PATH, scroll: true, mock: { piece: MOCK_PIECE_COMPLETE } },
  piece_review: {
    path: PIECE_PATH,
    scroll: true,
    mock: { piece: MOCK_PIECE_REVIEW, workflow: { messages: MOCK_REVIEW_MESSAGES, versions: MOCK_REVIEW_VERSIONS } },
  },
  piece_scheduling: {
    path: PIECE_PATH,
    scroll: true,
    mock: { piece: MOCK_PIECE_SCHEDULING, workflow: { messages: MOCK_REVIEW_MESSAGES, versions: MOCK_REVIEW_VERSIONS, social: MOCK_SOCIAL } },
  },
  list_group: { path: listPath, mock: { list: { ...MOCK_LIST, selectConfig: true } } },
};
